import get from 'lodash/get';
import isNumber from 'lodash/isNumber';

import { REGIONS, tagMap } from '@packages/constants';
import {
  ADD_BOOST_LABEL,
  BOOST_CATEGORY,
  BOOST_CODE,
  BOOST_TYPES,
  BOOST_UI_STATUS,
  Boost,
  BoostModalType,
  BoostTagType,
  DATE_TIME_DURATION,
  EmcDataType,
  PrimaryTravellerEligibleParams,
  Region,
  RegionCode,
  TRIP_STATUS,
  Traveller,
  Trip,
  TripDurationParams,
} from '@packages/types';

import { getAge, parseISO } from '../datetime/datetime';
import { getPayPerDayRate, isTripFreeOfCharge } from '../trip/common';

/**
 * Returns an array of all child boosts in the boost list that are dependent on the parent boost
 * @param parentBoost
 * @param boosts
 */
export const getBoostsThatDependOnThisBoost = (parentBoost: Boost, boosts: Boost[]) =>
  boosts.filter(b => b?.boostProperty?.anyDependentBoostIds?.includes(parentBoost.boostId)) ?? [];

/**
 * Checks if boost is dependent on a parent boost and will evaluate if it is selected or not
 * If there are no parent, it will default to true
 * @param childBoost
 * @param boosts
 */
export const isParentSelected = (childBoost: Boost, boosts: Boost[]) => {
  if (!childBoost?.boostProperty?.anyDependentBoostIds) {
    return true;
  }

  const parentBoost = boosts.filter(it =>
    childBoost?.boostProperty?.anyDependentBoostIds?.includes(it.boostId),
  );

  return parentBoost?.some(it => it.toUpdate?.isAdded ?? it.isAdded);
};

/**
 * Returns a list of all added children of the added parent.
 * @param parentBoost
 * @param boosts
 */
export const getAddedDependentChildrenBoosts = (
  parentBoost: Boost | undefined,
  boosts: Boost[] | undefined,
): Boost[] => {
  const isParentBoostAdded = parentBoost?.toUpdate?.isAdded ?? parentBoost?.isAdded;
  if (!parentBoost || !boosts || !isParentBoostAdded) {
    return [];
  }

  const listOfChildBoosts = getBoostsThatDependOnThisBoost(parentBoost, boosts);
  return listOfChildBoosts.filter(child => {
    return child?.toUpdate?.isAdded ?? child.isAdded;
  });
};

/**
 * Returns a boolean value that represents if the boost has anyDependentBoostIds in the boostProprery
 * attribute
 * @param childBoost
 */
export const hasDependencies = (childBoost: Boost) =>
  (childBoost.boostProperty?.anyDependentBoostIds || []).length > 0;

/**
 * Returns a boolean value representing if the boost is the first dependent in the boost list
 * @param childBoost
 * @param boosts
 */
export const isFirstDependant = (childBoost: Boost, boosts: Boost[]) => {
  const index = boosts.findIndex(({ boostId }) => boostId === childBoost.boostId);

  if (get(boosts, index - 1) && hasDependencies(get(boosts, index - 1)) === false) {
    return true;
  }
  return false;
};

/**
 * returns a boolen value representing if the boost is the last dependent in the boost list
 * @param childBoost
 * @param boosts
 */
export const isLastDependant = (childBoost: Boost, boosts: Boost[]) => {
  const index = boosts.findIndex(({ boostId }) => boostId === childBoost.boostId);

  if (get(boosts, index + 1) && hasDependencies(get(boosts, index + 1)) === false) {
    return true;
  }
  return false;
};

/**
 * Returns a boolean value representing if the boost has been selectively not added
 * @param boost
 */
export const isBoostExcluded = (boost: Boost) => boost.isSelected && !boost.isAdded;

/**
 * Returns the add label for appropriate boosts
 * @param boost
 */
export const getAddBoostLabel = (boost: Boost) => {
  const boostPrice = boost.toUpdate ? boost.toUpdate.adjustedPrice : parseInt(boost.price, 10);

  if (boost?.code === 'LUGG' && boostPrice === 0) {
    return ADD_BOOST_LABEL.ADD_ITEMS;
  }

  // This is for showing 'Select Limit' if the boost is "Extra Cancellation" and there is no price selected.
  if (boost?.code === 'CANX' && boostPrice === 0) {
    return ADD_BOOST_LABEL.SELECT_LIMIT;
  }

  // Show customised label for Snow Sports (SSPFREE) and Cruise Cover (CRSF)
  if (boost.code === BOOST_CODE.SNOW_SPORTS || boost.code === BOOST_CODE.CRUISE_COVER) {
    return ADD_BOOST_LABEL.YES;
  }

  return ADD_BOOST_LABEL.ADD_TO_TRIP;
};

/**
 * Returns the exclude boost label for appropriate boosts
 * @param boost
 */
export const getExcludeBoostLabel = (boost: Boost) => {
  // Show customised label for Snow Sports (SSFREE) and Cruise Cover (CRSF)
  if (['SSFREE', 'CRSF'].includes(boost?.code)) {
    return 'No';
  }
  return 'No Thanks';
};

export const isPayPerDayBoost = (boost?: Boost) => {
  if (boost?.toUpdate) {
    return isNumber(boost?.toUpdate?.boostProperty?.payPerDayRate);
  }

  return isNumber(boost?.boostProperty?.payPerDayRate);
};

/**
 * Returns true if the boost has mutually exclusive ids
 * @param selectedBoost
 * @param boostList
 */
export const doesBoostHaveMutuallyExclusiveIds = (
  selectedBoost?: Boost,
  boostList?: Array<Boost>,
) => {
  return (
    Array.isArray(selectedBoost?.boostProperty?.mutuallyExclusiveBoostIds) &&
    (selectedBoost?.boostProperty?.mutuallyExclusiveBoostIds?.length ?? 0) > 0 &&
    boostList
  );
};

/**
 * Decides on what success or failure callback to run based on the trips current screen
 * @param region
 * @param trip
 * @param onSuccess
 * @param onFailure
 */
export const complianceCheck = ({
  region,
  trip,
  onSuccess,
  onFailure,
}: {
  region: Region;
  trip: Trip;
  onSuccess: () => void;
  onFailure: (boostCategory: BOOST_CATEGORY | undefined) => void;
}) => {
  if (region.country === REGIONS.AU.code || region.country === REGIONS.US.code) {
    switch (trip?.currentScreen) {
      case BOOST_CATEGORY.BOOST_SUMMARY:
        return onSuccess();
      case BOOST_CATEGORY.MY_ACTIVITIES:
        return onSuccess();
      case BOOST_CATEGORY.MY_HEALTH:
        return onFailure(BOOST_CATEGORY.MY_HEALTH);
      case BOOST_CATEGORY.MY_STUFF:
        return onFailure(BOOST_CATEGORY.MY_STUFF);
      default:
        return onFailure(BOOST_CATEGORY.MY_HEALTH);
    }
  }
};

/**
 * Returns a boolean value if the input dates affect the original boost pay per day dates
 * @param tripStartDate
 * @param tripEndDate
 * @param editingBoost
 * @param country
 */
export const isPayPerDayDurationAffected = (
  {
    tripStartDate,
    tripEndDate,
    country,
  }: { tripStartDate: string; tripEndDate: string; country?: RegionCode },
  editingBoost: Boost,
) => {
  for (const dateRange of editingBoost.startEndDates) {
    const isPayPerDayStartDateIncluded =
      dateRange.startDate &&
      parseISO(dateRange.startDate, country) >= parseISO(tripStartDate, country);

    const isPayPerDayEndDateIncluded =
      dateRange.endDate && parseISO(dateRange.endDate, country) <= parseISO(tripEndDate, country);

    const result = !(isPayPerDayStartDateIncluded && isPayPerDayEndDateIncluded);

    if (result) {
      return true;
    }
  }

  return false;
};

/**
 *Returns a boolean representing if the boost is added.
 * @param boost
 */
export const isBoostAdded = (boost?: Boost) => {
  return !!(boost?.toUpdate?.isAdded ?? boost?.isAdded);
};

/**
 * Returns boolean value representing if this boost is Eligible
 * @param boost
 * @param region
 */
export const isBoostEligible = (boost?: Boost, region?: Region | null) => {
  if (!boost) return false;
  if (
    region &&
    region.country === REGIONS.US.code &&
    boost.code === BOOST_CODE.PRE_TRIP_CANCELLATION
  ) {
    return boost.isEligible;
  } else {
    return true;
  }
};

/**
 * Returns true if boost toggleIsDisabled. For extra cancellation AU region.
 * @param data
 * @param trip
 * @param isAU
 */
export const isBoostToggleDisabled = (
  data: Boost | undefined,
  trip: Trip | null,
  isAU: boolean,
) => {
  //CANX cannot be remove once customer paid for it
  return trip?.state === TRIP_STATUS.PAID && data?.code === 'CANX' && isAU;
};
export const getSpecifiedItemCoverValues = (boost?: Boost) =>
  boost?.toUpdate?.boostProperty?.coverAmountValues ?? boost?.boostProperty?.coverAmountValues;

export const getBoostExtraCoverValues = (boost?: Boost) =>
  boost?.toUpdate ? boost?.toUpdate?.boostProperty?.options : boost?.boostProperty?.options;

/**
 * Returns the boost selectedCoverAmountValues
 * If coverAmountValues does not exist, it will return undefined.
 * @param boost
 */
export const getBoostSelectedExtraCover = (boost?: Boost) =>
  boost?.toUpdate
    ? boost?.toUpdate?.boostProperty?.selectedExtraCover
    : boost?.boostProperty?.selectedExtraCover;

/**
 * Returns an array of specified items if exists in the boost.
 * If specified items does not exist, it will return undefined
 * @param boost
 */
export const getBoostSpecifiedItems = (boost?: Boost) =>
  boost?.toUpdate
    ? boost?.toUpdate?.boostProperty?.specifiedItems
    : boost?.boostProperty?.specifiedItems;

/**
 * returns the boost price
 * @param boost
 */
export const getBoostPrice = (boost?: Boost) =>
  boost?.toUpdate ? boost?.toUpdate?.adjustedPrice : parseFloat(boost?.price ?? '');

/**
 * Returns a boolean value representing if the boost is selected.
 * @param trip
 * @param boost
 */
export const getIsBoostSelected = (trip: Trip | null, boost?: Boost) => {
  const isPaid = trip?.state === TRIP_STATUS.PAID || trip?.state === TRIP_STATUS.UPDATING;
  return !isPaid || !boost?.toUpdate ? boost?.isAdded : boost?.toUpdate.isAdded;
};

/**
 * Finds the traveller by id and returns their first and last name.
 * If the traveller does not exist it will return null.
 * @param travellerId
 * @param travellers
 */
export const getTravellerName = (travellerId: string, travellers: Traveller[]) => {
  const selectedTraveller = travellers?.find(item => item.sortKey === travellerId);
  if (selectedTraveller) {
    return `${selectedTraveller?.firstName} ${selectedTraveller?.lastName ?? ''}`;
  } else {
    return null;
  }
};

export const getSpecifiedItemsAfterRemove = (itemId: string, boost?: Boost) =>
  boost?.toUpdate && boost?.toUpdate.boostProperty
    ? boost?.toUpdate?.boostProperty?.specifiedItems?.filter(
        specifiedItem => specifiedItem.itemId !== itemId,
      )
    : boost?.boostProperty.specifiedItems?.filter(specifiedItem => specifiedItem.itemId !== itemId);

export const getRemovedSpecifiedItems = (itemId: string, boost?: Boost) =>
  boost?.toUpdate && boost?.toUpdate?.boostProperty
    ? boost?.toUpdate?.boostProperty?.specifiedItems?.find(
        specifiedItem => specifiedItem.itemId === itemId,
      )
    : boost?.boostProperty?.specifiedItems?.find(specifiedItem => specifiedItem.itemId === itemId);

export const getExistingSpecifiedItems = (boost: Boost | undefined) => {
  if (
    boost?.toUpdate &&
    boost?.toUpdate?.boostProperty &&
    boost?.toUpdate.boostProperty?.specifiedItems
  ) {
    return boost?.toUpdate?.boostProperty?.specifiedItems;
  }

  if (boost?.toUpdate) {
    return [];
  }

  if (boost?.boostProperty && boost?.boostProperty?.specifiedItems) {
    return boost?.boostProperty?.specifiedItems;
  }

  return [];
};

export const getEmcBoost = (trip: Trip | null) => {
  return trip?.boosts.find(boost => boost.code === 'EMC');
};

/**
 * Returns a boolean representing if the boost is specified items
 * @param boost
 */
export const isBoostSpecifiedItems = (boost?: Boost) => boost?.code === BOOST_TYPES.SPECIFIED_ITEMS;
/**
 * Returns a boolean representing if the boost is extra cancellation
 * @param boost
 */
export const isBoostExtraCancellation = (boost?: Boost) =>
  boost?.code === BOOST_TYPES.EXTRA_CANCELLATION;

/**
 * Returns a boolean representing if the boost is gadget cover
 * @returns
 */
export const isBoostGadgetCover = (boost?: Boost) => boost?.code === BOOST_TYPES.GADGET_COVER;

/**
 * Returns a boolean representing if the boost is either specified items or extra cancellation
 * @param boost
 */
export const isBoostSpecifiedItemsOrExtraCancellation = (boost?: Boost): boolean =>
  isBoostSpecifiedItems(boost) || isBoostExtraCancellation(boost);

/**
 * Checks if the payPerDay Boost is free of charge with the promotion code applied
 * @param trip
 * @param boost
 */
export const isPayPerDayFree = (trip: Trip | null, boost?: Boost | null) =>
  getPayPerDayRate(boost) === 0 && !!trip?.promotionCode?.isFreeOfCharge;

/**
 * Returns a boolean representing if the boost has a price
 * @param boost
 */
export const doesBoostHavePrice = (boost: Boost) =>
  parseInt(boost.price, 10) > 0 || (boost?.toUpdate?.adjustedPrice ?? 0) > 0;

/**
 * Checks to see if the trip has been in post purchase once
 * @param trip
 */
export const isTripPostPurchase = (trip: Trip | null): boolean =>
  trip?.state === TRIP_STATUS.PAID || !!trip?.pendingUpdates;

/**
 * Disables the switch for extra cancellation on post purchase
 * @param boost
 * @param trip
 */
export const isPaidBoostCardToggleDisabled = (boost: Boost, trip: Trip) => {
  //CANX cannot be remove once customer paid for it
  return isTripPostPurchase(trip) && isBoostExtraCancellation(boost);
};

/**
 * Returns a boolean value representing if the boost has duration greater than 0
 * @param boost
 */
export const doesBoostHaveSelectedDays = (boost?: Boost): boolean => {
  if (!boost) {
    return false;
  }

  if (boost?.toUpdate) {
    return boost?.toUpdate.duration > 0;
  }

  return boost?.duration > 0;
};

/**
 * Returns true if the boost is either toggling whilst adding or removing
 * @param boost
 */
export const isBoostToggleLoading = (boost?: Boost): boolean =>
  boost?.toggleStatus === BOOST_UI_STATUS.ADDING ||
  boost?.toggleStatus === BOOST_UI_STATUS.REMOVING;

/**
 * Returns a boolean value representing if the boost is added to a free of charge trip
 * @param boost
 * @param trip
 */
export const isBoostAddedToFreeOfChargeTrip = (boost: Boost | undefined, trip: Trip | null) =>
  isTripFreeOfCharge(trip) && (boost?.toUpdate?.isAdded ?? boost?.isAdded);

/**
 * Returns a boolean value representing if the paid boost can be removed.
 * @param boost
 * @param editTrip
 */
export const canRemovePaidTripBoostListItem = (boost: Boost, editTrip: Trip | null) =>
  !(editTrip?.isTripStarted && boost.category === BOOST_CATEGORY.MY_STUFF);

/**
 * Grabs the modal type of the boost
 * @param boost
 */
export function getBoostModalType(boost?: Boost): BoostModalType | null {
  if (!boost) {
    return null;
  }

  if (isBoostSpecifiedItems(boost)) {
    return 'addSpecifiedItems';
  }

  if (isBoostExtraCancellation(boost)) {
    return 'extraCancellation';
  }

  if (isPayPerDayBoost(boost)) {
    return 'payPerDayCalendar';
  }

  return null;
}

/**
 * Returns an array of mutuallyExclusiveBoostIDs,
 * It will return an empty array if the boost does not have any.
 * @param boost
 */
export const getMutuallyExclusiveBoostIds = (boost?: Boost) => {
  return boost?.boostProperty?.mutuallyExclusiveBoostIds ?? [];
};

/**
 * Returns the boost category by route name
 * @param routeName
 */
export const getBoostCategoryByRouteName = (routeName: 'MyActivities' | 'MyStuff') =>
  routeName === 'MyActivities' ? BOOST_CATEGORY.MY_ACTIVITIES : BOOST_CATEGORY.MY_STUFF;

/**
 * get a trip duration based on date time
 *
 * @param startDate
 * @param endDate
 * @param regionCode
 * @param duration
 */
export const getTripDuration = ({
  startDate,
  endDate,
  regionCode,
  duration,
}: TripDurationParams) => {
  const isoStartDate = parseISO(startDate, regionCode);
  const isoEndDate = parseISO(endDate, regionCode);
  return isoEndDate.diff(isoStartDate, duration);
};

/**
 * check if boost is added
 *
 * @param trip
 * @param boostCode
 */
export const isBoostAddedOnTrip = (trip: Trip, boostCode: string) => {
  const boost = trip.boosts.find(boost => boost.code === boostCode);
  return isBoostAdded(boost);
};

/**
 * check if luggage boost modal should open
 *
 * @param trip
 * @param regionCode
 * @param threshold
 */
export const shouldOpenLuggageBoostUpsellModal = (
  trip: Trip,
  regionCode: RegionCode | undefined,
  threshold: number,
) => {
  const params: TripDurationParams = {
    startDate: trip.startDate,
    endDate: trip.endDate,
    regionCode: regionCode || 'AU',
    duration: DATE_TIME_DURATION.WEEK,
  };
  return (
    !isBoostAddedOnTrip(trip, BOOST_CODE.LUGGAGE) && getTripDuration(params).weeks >= threshold
  );
};

/**
 * check if primary traveller is eligible for upsell
 *
 * @param dob
 * @param maxAge
 * @param minAge
 * @param regionCode
 */
export const isPrimaryTravellerAgeEligible = ({
  dob,
  maxAge,
  minAge,
  regionCode,
}: PrimaryTravellerEligibleParams) => {
  if (!dob) {
    return;
  }
  const primaryTravellerAge = getAge(dob, regionCode);
  return primaryTravellerAge >= minAge && primaryTravellerAge <= maxAge;
};

/**
 * has destination selected on trip
 *
 * @param trip
 * @param countryCode
 * @param longName
 */

export const hasDestinationSelectedOnTrip = (trip: Trip, countryCode: string, longName: string) => {
  return trip.destinations.find(
    destination => destination.countryCode === countryCode && destination.longName === longName,
  );
};

export const getSelectedExtraCoverPrice = (boost?: Boost) => {
  const price = boost?.toUpdate?.adjustedPrice ?? boost?.price;
  return price ?? null;
};

/**
 * get my health boost from trip which is mandatory
 * @param trip
 */
export const getMyHealthMandatoryBoost = (trip?: Trip | null) => {
  return trip?.boosts?.find(
    boost => boost.category === BOOST_CATEGORY.MY_HEALTH && boost.isMandatory === true,
  );
};

export function isEmcRequireRedeclare(emcData?: EmcDataType) {
  return !!emcData?.requireRedeclare;
}

export function validateEmcTravellerCanProceed(emcTraveller?: EmcDataType) {
  if (!emcTraveller) return false;
  const isSelected = emcTraveller?.isSelected;
  const hasEvaluatedMedicalConditions = !!emcTraveller?.medicalConditions?.length;
  const isRequireRedeclared = isEmcRequireRedeclare(emcTraveller) && !!emcTraveller.isCompleted;
  const hasNotSelectedDeclaration = isSelected === undefined;
  const hasDeclaredAndSubmittedScreening = isSelected === true && hasEvaluatedMedicalConditions;
  const hasDeclaredNoAndNotSubmittedScreening =
    isSelected === false && !hasEvaluatedMedicalConditions;

  if (hasNotSelectedDeclaration) {
    return false;
  }

  if (isRequireRedeclared) {
    return false;
  }

  if (hasDeclaredNoAndNotSubmittedScreening) {
    return true;
  }

  return hasDeclaredAndSubmittedScreening;
}

export function getEmcTravellerErrorType(emcTraveller?: EmcDataType) {
  if (!emcTraveller) return 'NO_DATA' as const;
  const isSelected = emcTraveller?.isSelected;
  const hasEvaluatedMedicalConditions = !!emcTraveller?.isCompleted;
  const isRequireRedeclared = isEmcRequireRedeclare(emcTraveller) && hasEvaluatedMedicalConditions;
  const hasNotSelectedDeclaration = isSelected === undefined;
  const hasDeclaredYesAndNotSubmittedScreening =
    isSelected === true && !hasEvaluatedMedicalConditions;
  const hasDeclaredNoAndNotSubmittedScreening =
    isSelected === false && !hasEvaluatedMedicalConditions;

  if (hasNotSelectedDeclaration) {
    return 'YES_AND_NO_NOT_SELECTED' as const;
  }

  if (isRequireRedeclared) {
    return 'REQUIRES_REDECLARATION' as const;
  }

  if (hasDeclaredYesAndNotSubmittedScreening) {
    return 'YES_SELECTED_BUT_REQUIRES_SCREENING' as const;
  }

  if (hasDeclaredNoAndNotSubmittedScreening) {
    return undefined;
  }
  // All other scenarios are valid
  return undefined;
}

export function hasEmcDataForAllTravellers(emcBoost: Boost | undefined) {
  return emcBoost?.travellers?.length === emcBoost?.boostProperty?.emcData?.length;
}

export function isEmcBoostAdded(emcData?: EmcDataType) {
  return !!emcData?.isAdded;
}

export function hasAddedEmcBoost(trip: Trip | null) {
  return getEmcBoost(trip)?.boostProperty?.emcData?.some(isEmcBoostAdded) ?? false;
}

export function getEmcAssessments(trip: Trip | null) {
  const emcBoost = getEmcBoost(trip);

  if (!emcBoost) {
    return [];
  }

  return emcBoost.boostProperty.emcData?.filter(({ assessmentNumber }) => !!assessmentNumber) ?? [];
}

export const getBoostTagNameAndVariant = (boost?: Boost, isEligible?: boolean) => {
  if (boost?.code === BOOST_CODE.PRE_TRIP_CANCELLATION) {
    return isEligible ? tagMap['RECOMMENDED'] : tagMap['RECOMMENDED_DISABLED'];
  }
  if (!boost || !boost.tags || boost.tags.length === 0) {
    return null;
  }

  const boostTag = boost.tags[0];

  const badgeTag = boostTag && tagMap[boostTag];

  return badgeTag ?? null;
};

export const isPopularTagPresent = (boosts: Boost[] | undefined): boolean => {
  if (!boosts || !Array.isArray(boosts)) {
    return false;
  }

  const includedTags: BoostTagType[] = ['POPULAR'];

  return boosts.some(
    boost =>
      Array.isArray(boost.tags) &&
      boost.tags.length > 0 &&
      boost.tags.some(tag => includedTags.includes(tag)),
  );
};

export function isBoostRequired(boost?: Boost) {
  return !!boost?.tags?.includes('REQUIRED');
}

export function isBoostCruiseCover(boost?: Boost) {
  return boost?.code === 'CRSF';
}

export function getBoostExcessLevels(boost: Boost) {
  return boost.boostProperty.availableExcessLevels;
}

export function getBoostInclusionBullets(boost: Boost) {
  return boost.template?.inclusions?.find(({ type }) => type === 'bullet')?.value;
}
